import {FC} from "react";
import {Document, Page, Text, View, StyleSheet, Font} from "@react-pdf/renderer";
import dayjs from "dayjs";

// Import local font files
import TitilliumWebRegular from "../../../../assets/fonts/TitilliumWeb-Regular.ttf";
import TitilliumWebSemiBold from "../../../../assets/fonts/TitilliumWeb-SemiBold.ttf";
import TitilliumWebBold from "../../../../assets/fonts/TitilliumWeb-Bold.ttf";

// Register Titillium Web font family
Font.register({
	family: "Titillium Web",
	fonts: [
		{ src: TitilliumWebRegular, fontWeight: 400 },
		{ src: TitilliumWebSemiBold, fontWeight: 600 },
		{ src: TitilliumWebBold, fontWeight: 700 },
	]
});

// Color palette - refined design (matching UI)
const colors = {
	primary: "#FF9E00", // Orange accent
	background: "#FFFFFF", // White for PDF (print-friendly)
	cardBackground: "#F5F5F5", // Light gray for cards (matching UI)
	textPrimary: "#1A1A2E", // Dark navy text
	textSecondary: "#6B7280", // Muted gray text
	textMuted: "#9CA3AF", // Light gray for labels
	border: "#E5E7EB", // Subtle border
	headerBg: "#1A1A2E", // Dark navy header
	headerText: "#FFFFFF", // White text on dark background
	success: "#4CAF50", // Green for positive (matching UI)
	error: "#F44336", // Red for negative (matching UI)
	accentBlue: "#3B82F6" // Blue accent
};

// Page dimensions (A4 in points)
const PAGE_PADDING = 40;
const A4_WIDTH = 595;
const AVAILABLE_WIDTH = A4_WIDTH - (PAGE_PADDING * 2); // 515 points

// Card grid calculations - 2 columns with gap
const CARD_GAP = 12;
const CARD_WIDTH = (AVAILABLE_WIDTH - CARD_GAP) / 2; // ~251 points each

const styles = StyleSheet.create({
	page: {
		padding: 40,
		fontSize: 10,
		fontFamily: "Titillium Web",
		backgroundColor: colors.background,
		color: colors.textPrimary
	},
	// Header section
	headerSection: {
		marginBottom: 24
	},
	title: {
		fontSize: 28,
		marginBottom: 4,
		fontWeight: "bold",
		color: colors.textPrimary
	},
	subtitle: {
		fontSize: 11,
		color: colors.textSecondary
	},
	// Overview cards grid - 2 columns
	overviewCardsContainer: {
		marginBottom: 20
	},
	overviewCardsRow: {
		flexDirection: "row",
		justifyContent: "space-between",
		marginBottom: CARD_GAP
	},
	card: {
		width: CARD_WIDTH,
		backgroundColor: colors.cardBackground,
		borderRadius: 8,
		padding: 14,
		minHeight: 70,
		border: `1 solid ${colors.border}`
	},
	cardHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 6
	},
	cardTitle: {
		fontSize: 10,
		fontWeight: "bold",
		textTransform: "uppercase",
		color: colors.textPrimary,
		letterSpacing: 0.3
	},
	cardChangeBadge: {
		flexDirection: "row",
		alignItems: "center"
	},
	cardChangeBadgePositive: {},
	cardChangeBadgeNegative: {},
	cardChangeText: {
		fontSize: 9,
		fontWeight: "bold"
	},
	cardChangePositive: {
		color: colors.success
	},
	cardChangeNegative: {
		color: colors.error
	},
	cardValue: {
		fontSize: 24,
		fontWeight: "normal",
		color: colors.primary // Orange value like UI
	},
	cardValueSmall: {
		fontSize: 21,
		fontWeight: "normal",
		color: colors.primary // Orange value like UI - slightly smaller for watch time
	},
	// Explanation text
	explanationText: {
		fontSize: 8,
		marginTop: 8,
		marginBottom: 24,
		color: colors.textMuted,
		fontStyle: "italic"
	},
	// Table styles
	tableContainer: {
		marginBottom: 30,
		backgroundColor: colors.cardBackground,
		borderRadius: 8,
		border: `1 solid ${colors.border}`,
		overflow: "hidden",
		width: "100%",
		maxWidth: AVAILABLE_WIDTH
	},
	tableTitle: {
		fontSize: 12,
		fontWeight: "bold",
		padding: 16,
		paddingBottom: 12,
		color: colors.textPrimary,
		textTransform: "uppercase",
		letterSpacing: 0.5
	},
	tableHeader: {
		flexDirection: "row",
		paddingHorizontal: 16,
		paddingVertical: 10,
		borderBottom: `1 solid ${colors.border}`
	},
	tableRow: {
		flexDirection: "row",
		paddingHorizontal: 16,
		paddingVertical: 10,
		borderBottom: `1 solid ${colors.border}`,
		backgroundColor: colors.cardBackground
	},
	tableRowLast: {
		borderBottom: "none"
	},
	tableCell: {
		padding: 4,
		flex: 1,
		flexWrap: "wrap",
		color: colors.textSecondary,
		fontSize: 9,
		textTransform: "uppercase"
	},
	tableCellHeader: {
		fontWeight: "bold",
		flexWrap: "wrap",
		color: colors.textPrimary,
		fontSize: 10,
		textTransform: "capitalize"
	},
	tableCellWide: {
		padding: 4,
		flex: 2,
		flexWrap: "wrap",
		color: colors.textSecondary,
		fontSize: 9,
		textTransform: "uppercase"
	},
	tableCellMedium: {
		padding: 4,
		flex: 1.5,
		flexWrap: "wrap",
		color: colors.textSecondary,
		fontSize: 9,
		textTransform: "uppercase"
	},
	tableCellNarrow: {
		padding: 4,
		flex: 0.8,
		flexWrap: "wrap",
		color: colors.textSecondary,
		fontSize: 9,
		textTransform: "uppercase"
	},
	// Top Performers styles
	performersContainer: {
		marginBottom: 30,
		backgroundColor: colors.cardBackground,
		borderRadius: 8,
		border: `1 solid ${colors.border}`,
		padding: 16
	},
	performersTitle: {
		fontSize: 12,
		fontWeight: "bold",
		marginBottom: 16,
		color: colors.textPrimary,
		textTransform: "uppercase",
		letterSpacing: 0.5
	},
	performersGrid: {
		flexDirection: "row",
		justifyContent: "space-between"
	},
	performerCard: {
		width: "31%",
		alignItems: "flex-start"
	},
	performerAvatar: {
		width: 60,
		height: 60,
		borderRadius: 30,
		marginBottom: 8,
		backgroundColor: colors.border,
		border: `2 solid ${colors.primary}`
	},
	performerAvatarPlaceholder: {
		width: 60,
		height: 60,
		borderRadius: 30,
		marginBottom: 8,
		backgroundColor: "#E0E0E0",
		border: `2 solid ${colors.primary}`,
		justifyContent: "center",
		alignItems: "center"
	},
	performerAvatarText: {
		fontSize: 24,
		color: colors.textMuted,
		fontWeight: "bold"
	},
	performerName: {
		fontSize: 12,
		fontWeight: "bold",
		color: colors.textPrimary,
		marginBottom: 6
	},
	performerStat: {
		marginBottom: 4,
		width: "100%"
	},
	performerStatIcon: {
		width: 14,
		height: 14,
		marginRight: 6
	},
	performerStatText: {
		fontSize: 8,
		color: colors.textSecondary,
		textTransform: "uppercase",
		flexWrap: "wrap"
	}
});

interface ScheduledReportsPDFProps {
	overview: any;
	topPerformers: any[];
	usersWithout: any[];
	divisions: any[];
	supervisors: any[];
	monthName: string;
	accountName: string;
}

export const ScheduledReportsPDFDocument: FC<ScheduledReportsPDFProps> = ({
	overview,
	topPerformers,
	usersWithout,
	divisions,
	supervisors,
	monthName,
	accountName
}) => {
	const renderTable = (title: string, headers: string[], rows: any[], getRowData: (row: any) => string[], cellWidths?: string[], allowPageBreak = false) => {
		return (
			<View style={styles.tableContainer} wrap={allowPageBreak}>
				<Text style={styles.tableTitle}>{title}</Text>
				<View style={styles.tableHeader} wrap={false}>
					{headers.map((header, idx) => {
						const cellStyle = cellWidths?.[idx] === "wide" ? styles.tableCellWide :
							cellWidths?.[idx] === "medium" ? styles.tableCellMedium :
							cellWidths?.[idx] === "narrow" ? styles.tableCellNarrow :
							styles.tableCell;
						return (
							<View key={idx} style={cellStyle}>
								<Text style={styles.tableCellHeader} wrap>{header}</Text>
							</View>
						);
					})}
				</View>
				{rows.map((row, rowIdx) => (
					<View key={rowIdx} style={[styles.tableRow, rowIdx === rows.length - 1 && styles.tableRowLast]} wrap={allowPageBreak}>
						{getRowData(row).map((cell, cellIdx) => (
							<Text key={cellIdx} style={
								cellWidths?.[cellIdx] === "wide" ? styles.tableCellWide :
								cellWidths?.[cellIdx] === "medium" ? styles.tableCellMedium :
								cellWidths?.[cellIdx] === "narrow" ? styles.tableCellNarrow :
								styles.tableCell
							}>{cell || "-"}</Text>
						))}
					</View>
				))}
			</View>
		);
	};
	
	const renderTopPerformers = (performers: any[]) => {
		// Only show performers that exist (up to 3)
		const displayPerformers = performers.slice(0, 3);
		
		if (displayPerformers.length === 0) {
			return (
				<View style={styles.performersContainer}>
					<Text style={styles.performersTitle}>Top 3 Performers</Text>
					<Text style={{fontSize: 10, color: colors.textSecondary}}>No performer data available for this period.</Text>
				</View>
			);
		}
		
		return (
			<View style={styles.performersContainer}>
				<Text style={styles.performersTitle}>Top 3 Performers</Text>
				<View style={styles.performersGrid}>
					{displayPerformers.map((performer, idx) => (
						<View key={idx} style={styles.performerCard}>
							{/* Avatar placeholder - react-pdf doesn't support external images well */}
							<View style={styles.performerAvatarPlaceholder}>
								<Text style={styles.performerAvatarText}>
									{performer.Name?.charAt(0) || "?"}
								</Text>
							</View>
							<Text style={styles.performerName}>
								{performer.Name || "Unknown"}
							</Text>
							<Text style={styles.performerStatText}>
								{performer["Completed Lessons This Month"] || 0} lessons this month
							</Text>
							<Text style={styles.performerStatText}>
								{performer.Streak || 0} day streak
							</Text>
							<Text style={styles.performerStatText}>
								Supervisor: {performer.Supervisor || "N/A"}
							</Text>
							<Text style={styles.performerStatText}>
								Division: {performer.Division || "N/A"}
							</Text>
						</View>
					))}
				</View>
			</View>
		);
	};

	const renderOverviewCard = (title: string, value: string | number, changePercent?: number, reverseColors = false) => {
		const hasChange = changePercent !== undefined && changePercent !== null;
		const isPositive = hasChange && changePercent >= 0;
		// For overdue lessons: up arrow (more overdue) is bad (red), down arrow (less overdue) is good (green)
		// For other metrics: up arrow is good (green), down arrow is bad (red)
		// Use plus/minus signs that work reliably in PDF
		const arrow = hasChange ? (isPositive ? "+" : "-") : "";
		const changeText = hasChange ? `${arrow}${Math.abs(Math.round(changePercent))}%` : "";
		const isPositiveForColor = reverseColors ? !isPositive : isPositive;
		// Use smaller font for watch time values (containing "hrs")
		const isWatchTime = typeof value === "string" && value.includes("hrs");
		const valueStyle = isWatchTime ? styles.cardValueSmall : styles.cardValue;

		return (
			<View style={styles.card}>
				<View style={styles.cardHeader}>
					<Text style={styles.cardTitle}>{title}</Text>
					{hasChange && (
						<View style={[
							styles.cardChangeBadge,
							isPositiveForColor ? styles.cardChangeBadgePositive : styles.cardChangeBadgeNegative
						]}>
							<Text style={[
								styles.cardChangeText,
								isPositiveForColor ? styles.cardChangePositive : styles.cardChangeNegative
							]}>
								{changeText}
							</Text>
						</View>
					)}
				</View>
				<Text style={valueStyle}>{value}</Text>
			</View>
		);
	};

	// Calculate user percentages
	const usersWithCompletedCount = Number(overview?.["Users with Completed Lessons"]) || 0;
	const usersWithoutCount = Number(overview?.["Users without Completion"]) || 0;
	const totalUsers = usersWithCompletedCount + usersWithoutCount;
	const usersWithCompletedPercent = totalUsers > 0 ? ((usersWithCompletedCount / totalUsers) * 100).toFixed(1) : "0.0";
	
	// Format watch time as XX hrs XX min (or "XX hrs" if minutes are 0)
	const formatWatchTime = (hours: number) => {
		const h = Math.floor(hours);
		const m = Math.round((hours - h) * 60);
		if (m === 0) {
			return `${h} hrs`;
		}
		return `${h} hrs ${m.toString().padStart(2, "0")} min`;
	};

	return (
		<Document>
			{/* First Page - Title, Month, Overview Cards, Top Performers */}
			<Page size="A4" style={styles.page}>
				{/* Header Section */}
				<View style={styles.headerSection}>
					<Text style={styles.title}>{monthName.split(" ")[0]} Report</Text>
					<Text style={styles.subtitle}>Team engagement overview and insights</Text>
				</View>

				{/* Overview Cards - 2 column grid */}
				{overview && (
					<View style={styles.overviewCardsContainer}>
						{/* Row 1 */}
						<View style={styles.overviewCardsRow}>
							{renderOverviewCard(
								"Total Watch Time",
								formatWatchTime(overview["Total Watch Time (hours)"] || 0),
								overview["Watch Time Change %"]
							)}
							{renderOverviewCard(
								"Users With Completed Lessons",
								`${usersWithCompletedPercent}%`,
								overview["Users with Completed Lessons Change %"]
							)}
						</View>
						{/* Row 2 */}
						<View style={styles.overviewCardsRow}>
							{renderOverviewCard(
								"Average Watch Time",
								formatWatchTime(overview["Average Watch Time (hours)"] || 0),
								overview["Average Watch Time Change %"]
							)}
							{renderOverviewCard(
								"Completed Lessons",
								overview["Completed Lessons"] || 0,
								overview["Completed Lessons Change %"]
							)}
						</View>
						{/* Row 3 */}
						<View style={styles.overviewCardsRow}>
							{renderOverviewCard(
								"Completed Courses",
								overview["Completed Courses"] || 0,
								overview["Completed Courses Change %"]
							)}
							{renderOverviewCard(
								"Overdue Lessons",
								overview["Overdue Lessons"] || 0,
								overview["Overdue Lessons Change %"],
								true // reverseColors = true for overdue lessons
							)}
						</View>
						{/* Row 4 */}
						<View style={styles.overviewCardsRow}>
							{renderOverviewCard(
								"Average Streak",
								`${overview["Average Streak (days)"] || 0} days`,
								overview["Average Streak Change %"]
							)}
							{renderOverviewCard(
								"Utilization",
								`${overview["Utilization %"] || 0}%`,
								overview["Utilization Change %"]
							)}
						</View>
					</View>
				)}

				{/* Top Performers */}
				{renderTopPerformers(topPerformers || [])}
			</Page>

			{/* Second Page - Division Insights */}
			<Page size="A4" style={styles.page}>
				{renderTable(
					"Division Insights",
					["Division", "Team Size", "Total Watch Time", "Avg. Watch Time", "Total Lesson Completed", "Avg. Completed Lessons", "Overdue Lessons"],
					divisions || [],
					(row) => [
						row.Division || "",
						row["Team Size"]?.toString() || "",
						formatWatchTime(row["Total Watch Time (hours)"] || 0),
						formatWatchTime(row["Avg. Watch Time (hours)"] || 0),
						row["Total Lesson Completed"]?.toString() || "",
						row["Avg. Completed Lessons"]?.toString() || "",
						row["Overdue Lessons"]?.toString() || ""
					]
				)}
			</Page>

			{/* Third Page - Supervisor Insights */}
			<Page size="A4" style={styles.page}>
				{renderTable(
					"Supervisor Insights",
					["Supervisor", "Team Size", "Total Watch Time", "Avg. Watch Time", "Total Completed Lessons", "Avg. Completed Lessons", "Overdue Lessons"],
					supervisors || [],
					(row) => [
						row.Supervisor || "",
						row["Team Size"]?.toString() || "",
						formatWatchTime(row["Total Watch Time (hours)"] || 0),
						formatWatchTime(row["Avg. Watch Time (hours)"] || 0),
						row["Total Completed Lessons"]?.toString() || "",
						row["Avg. Completed Lessons"]?.toString() || "",
						row["Overdue Lessons"]?.toString() || ""
					]
				)}
			</Page>

			{/* Fourth Page - Users Without Completions (can flow to multiple pages) */}
			<Page size="A4" style={styles.page} wrap>
				{renderTable(
					`Users Without Completed Lessons (${usersWithout?.length || 0})`,
					["Name", "Division", "Supervisor", "Last Completion Date"],
					usersWithout || [],
					(row) => [
						row.Name || "",
						row.Division || "",
						row.Supervisor || "",
						row["Date of Last Completion"] ? dayjs(row["Date of Last Completion"]).format("MM/DD/YYYY") : "NEVER"
					],
					["wide", "medium", "medium", "medium"],
					true
				)}
			</Page>
		</Document>
	);
};

